import {styled} from '@mui/material/styles'
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
} from '@mui/material'

// ─── Color Tokens ───────────────────────────────────────────────────────────
const palette = {
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey600: '#757575',
  grey800: '#424242',
  white: '#ffffff',
}

// ─── Form Section ───────────────────────────────────────────────────────────
export const FormCard = styled(Paper)(({theme}) => ({
  padding: 0,
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
}))

export const FormHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
}))

export const FormHeaderIconBox = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: '#e3f2fd',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#1565c0',
  flexShrink: 0,
})

export const FormHeaderTextGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

export const FormSectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.85rem',
  color: palette.grey800,
  lineHeight: 1.3,
})

export const FormSectionSubtitle = styled(Typography)({
  fontSize: '0.72rem',
  color: palette.grey600,
  lineHeight: 1.4,
  marginTop: 1,
})

export const FormBody = styled(Box)(({theme}) => ({
  padding: theme.spacing(2, 2.5),
}))

export const FieldLabel = styled(Typography)(({theme}) => ({
  fontWeight: 600,
  fontSize: '0.72rem',
  color: palette.grey800,
  marginBottom: theme.spacing(0.5),
}))

export const FieldHelper = styled(Typography)({
  fontSize: '0.65rem',
  color: palette.grey600,
  marginTop: 3,
})

export const FormDivider = styled(Box)(({theme}) => ({
  borderTop: `1px solid ${palette.grey200}`,
  margin: theme.spacing(0, -2.5),
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}))

export const FormFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const FormFooterHint = styled(Typography)({
  fontSize: '0.68rem',
  color: palette.grey600,
  fontStyle: 'italic',
})

export const RunButton = styled(Button)(({theme}) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.75, 2.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}))

// ─── Results Banner ─────────────────────────────────────────────────────────
export const ResultsBanner = styled(Alert)(({theme}) => ({
  marginTop: theme.spacing(2),
  borderRadius: 12,
  '& .MuiAlert-message': {
    width: '100%',
  },
  '& .MuiAlert-icon': {
    alignItems: 'center',
  },
}))

export const ResultsBannerBody = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}))

export const ResultsBannerText = styled(Box)({
  flex: 1,
  minWidth: 0,
})

export const ResultsBannerActions = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexShrink: 0,
}))

export const BannerActionButton = styled(Button)(({theme}) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}))

// ─── KPI Metric Cards ──────────────────────────────────────────────────────
export const KpiRow = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}))

export const KpiCard = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}))

export const KpiCardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const KpiLabel = styled(Typography)({
  fontSize: '0.7rem',
  fontWeight: 600,
  color: palette.grey600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
})

export const KpiValue = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 800,
  color: palette.grey800,
  lineHeight: 1.2,
})

export const KpiDelta = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'positive',
})(({positive}) => ({
  fontSize: '0.72rem',
  fontWeight: 600,
  color: positive ? '#2e7d32' : '#c62828',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
}))

export const KpiIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})(({bgColor}) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: bgColor || palette.grey100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}))

export const KpiSubtext = styled(Typography)({
  fontSize: '0.68rem',
  color: palette.grey600,
  lineHeight: 1.3,
})

export const KpiInfoButton = styled(IconButton)({
  width: 20,
  height: 20,
  color: palette.grey300,
  '&:hover': {
    color: palette.grey600,
    backgroundColor: 'transparent',
  },
})

// ─── Tab Section Headers ───────────────────────────────────────────────────
export const TabSectionHeader = styled(Box)(({theme}) => ({
  marginBottom: theme.spacing(2.5),
}))

export const TabSectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: palette.grey800,
})

export const TabSectionSubtitle = styled(Typography)({
  fontSize: '0.75rem',
  color: palette.grey600,
  marginTop: 2,
})

// ─── Tabs / Results Section ─────────────────────────────────────────────────
export const ResultsContainer = styled(Paper)(({theme}) => ({
  marginTop: theme.spacing(2.5),
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
}))

export const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  minHeight: 48,
})

// ─── Allocation Summary Tab ─────────────────────────────────────────────────
export const AllocationSummaryHeader = styled(Box)(({theme}) => ({
  marginBottom: theme.spacing(2),
}))

export const AllocationSummaryTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1rem',
  color: palette.grey800,
})

export const AllocationSummarySubtitle = styled(Typography)({
  fontSize: '0.8rem',
  color: palette.grey600,
  marginTop: 2,
})

// ─── Data Grid Styles (used by dialogs) ─────────────────────────────────────
export const DataGridWrapper = styled(Box)({
  '& .MuiDataGrid-cell': {
    paddingTop: 12,
    paddingBottom: 12,
  },
})

export const CourierCellText = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.875rem',
})

// ─── Custom Allocation Table ────────────────────────────────────────────────
export const AllocTableCard = styled(Paper)({
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const AllocTableContainer = styled(Box)({
  overflowX: 'auto',
})

export const AllocTable = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  minWidth: 800,
})

export const AllocThead = styled('thead')({})

export const AllocTh = styled('th', {
  shouldForwardProp: (prop) => prop !== 'colColor' && prop !== 'isFirst',
})(({colColor, isFirst}) => ({
  padding: '12px 16px',
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: isFirst ? 'left' : 'center',
  borderBottom: `2px solid ${palette.grey200}`,
  whiteSpace: 'nowrap',
  ...(colColor
    ? {backgroundColor: colColor.bg, color: colColor.text}
    : {backgroundColor: palette.grey50, color: palette.grey600}),
}))

export const AllocTbody = styled('tbody')({})

export const AllocTr = styled('tr', {
  shouldForwardProp: (prop) => prop !== 'isLast',
})(({isLast}) => ({
  transition: 'background-color 0.12s ease',
  '&:hover': {
    backgroundColor: '#f5f9ff',
  },
  '& td': {
    borderBottom: isLast ? 'none' : `1px solid ${palette.grey200}`,
  },
}))

export const AllocTdCourier = styled('td')(({theme}) => ({
  padding: '14px 16px',
  verticalAlign: 'middle',
  backgroundColor: palette.grey50,
  borderRight: `1px solid ${palette.grey200}`,
}))

export const CourierNameRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
}))

export const CourierAvatar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})(({bgColor}) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: bgColor || palette.grey100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 800,
  color: palette.white,
  flexShrink: 0,
  textTransform: 'uppercase',
}))

export const CourierName = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.82rem',
  color: palette.grey800,
  lineHeight: 1.2,
})

export const CourierManualTag = styled(Typography)({
  fontSize: '0.65rem',
  color: palette.grey600,
  lineHeight: 1.2,
})

export const AllocTdValue = styled('td', {
  shouldForwardProp: (prop) => prop !== 'isHighest',
})(({isHighest}) => ({
  padding: '12px 16px',
  verticalAlign: 'middle',
  textAlign: 'center',
  ...(isHighest && {
    backgroundColor: '#f1f8e9',
  }),
}))

export const AllocCellContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})(({clickable}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  ...(clickable && {
    cursor: 'pointer',
    borderRadius: 8,
    padding: '4px 0',
    '&:hover': {
      backgroundColor: 'rgba(21, 101, 192, 0.04)',
    },
  }),
}))

export const AllocCountText = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: palette.grey800,
  lineHeight: 1,
})

export const AllocPctChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'chipColor',
})(({chipColor}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  borderRadius: 4,
  fontSize: '0.65rem',
  fontWeight: 700,
  backgroundColor: chipColor ? `${chipColor}25` : palette.grey100,
  color: chipColor || palette.grey600,
}))

export const AllocBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pct' && prop !== 'barColor',
})(({pct, barColor}) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: palette.grey100,
  width: '80%',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${Math.min(pct || 0, 100)}%`,
    borderRadius: 2,
    backgroundColor: barColor || '#90caf9',
    transition: 'width 0.4s ease',
  },
}))

export const AllocEmptyCell = styled(Typography)({
  fontSize: '0.8rem',
  color: palette.grey300,
  fontStyle: 'italic',
})

// ─── Dialog (shared between SimulationResultsDialog & ManualSimulationDetails)
export const SimDialog = styled(Dialog)(({theme}) => ({
  '& .MuiDialog-paper': {
    borderRadius: 0,
    maxHeight: '100%',
    boxShadow: 'none',
    [theme.breakpoints.up('sm')]: {
      borderRadius: 16,
      maxHeight: '90vh',
    },
  },
}))

export const SimDialogTitle = styled(DialogTitle)(({theme}) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
}))

export const SimDialogTitleRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

export const SimDialogHeading = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: palette.grey800,
})

export const SimDialogSubtitle = styled(Typography)({
  fontSize: '0.8rem',
  color: palette.grey600,
  marginTop: 4,
})

export const SimDialogContent = styled(DialogContent)(({theme}) => ({
  padding: theme.spacing(0, 3),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
}))

export const SimDialogEmptyState = styled(Box)(({theme}) => ({
  padding: theme.spacing(8, 2),
  textAlign: 'center',
  color: palette.grey600,
}))

export const SimDialogContentBody = styled(Box)(({theme}) => ({
  padding: theme.spacing(3, 0),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}))

export const SimDialogGridSection = styled(Box)({
  minHeight: 0,
  flex: '1 1 auto',
})

export const SimDialogActions = styled(DialogActions)(({theme}) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
  gap: theme.spacing(1),
}))

export const SimDialogCloseButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 2.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}))

// ─── Manual Simulation Details ──────────────────────────────────────────────
export const ManualDetailChip = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.75rem',
})

export const ManualTableSection = styled(Box)(({theme}) => ({
  border: `1px solid ${palette.grey200}`,
  borderRadius: 12,
  overflow: 'hidden',
}))

export const ManualTableHeader = styled(Box)(({theme}) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: palette.grey100,
  borderBottom: `1px solid ${palette.grey200}`,
}))

export const ManualTableTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.85rem',
  color: palette.grey800,
})

// ─── Shipment Detail Cards (Dialog) ─────────────────────────────────────────
export const ShipmentList = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}))

export const ShipmentCard = styled(Paper)({
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const ShipmentCardHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.25, 2),
  backgroundColor: palette.grey50,
  borderBottom: `1px solid ${palette.grey200}`,
}))

export const ShipmentAwb = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.82rem',
  color: palette.grey800,
  fontFamily: 'monospace',
})

export const ShipmentRemark = styled(Typography)({
  fontSize: '0.68rem',
  color: palette.grey600,
  fontStyle: 'italic',
})

export const ShipmentCardBody = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
})

export const ShipmentStrategyCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accentColor' && prop !== 'isBest',
})(({accentColor, isBest}) => ({
  padding: '12px 14px',
  borderRight: `1px solid ${palette.grey200}`,
  '&:last-child': {
    borderRight: 'none',
  },
  ...(isBest && {
    backgroundColor: '#f1f8e9',
  }),
}))

export const StrategyCellLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accentColor',
})(({accentColor}) => ({
  fontSize: '0.6rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: accentColor || palette.grey600,
  marginBottom: 8,
}))

export const StrategyCourierName = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.78rem',
  color: palette.grey800,
  lineHeight: 1.3,
  marginBottom: 6,
})

export const StrategyMetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 3,
})

export const StrategyMetaText = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 500,
  lineHeight: 1.2,
})

export const StrategyNa = styled(Typography)({
  fontSize: '0.75rem',
  color: palette.grey300,
  fontStyle: 'italic',
  paddingTop: 4,
})

export const ShipmentPaginationRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(2),
}))

export const PaginationInfo = styled(Typography)({
  fontSize: '0.75rem',
  color: palette.grey600,
  fontWeight: 500,
})

export const PaginationButtons = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
}))
